import { convertHEIC, delay } from '@dai/common'
import { Box, Stack } from '@mui/material'
import React from 'react'

import Loader from '@/components/Loader'
import useT from '@/i18n/useT'

function PhotoUpload({
  setImage,
  imagePreview,
  setImagePreview,
  Button,
  container = 'fit',
}: {
  setImage: (image: File) => void
  imagePreview: string | null | undefined
  setImagePreview: (imagePreview: string | null) => void
  Button: React.ReactElement
  container?: 'circle' | 'square' | 'fit'
}) {
  const { t } = useT()
  const [loadingImage, setLoadingImage] = React.useState(false)
  const handleImageChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0]
    if (file) {
      const acceptedTypes = [
        'image/jpeg',
        'image/png',
        'image/heic',
        'image/heif',
      ]
      if (!acceptedTypes.includes(file.type)) {
        alert(t('photoUpload.invalidImageFile'))
        return
      }
      setLoadingImage(true)
      await delay(500)
      if (file.type === 'image/heic' || file.type === 'image/heif') {
        const newFile = await convertHEIC({
          inputFile: file,
          outputFormat: 'JPEG',
          quality: 1,
        })

        setImage(newFile)
        const preview = URL.createObjectURL(newFile)
        setImagePreview(preview)
      } else if (file.type.startsWith('image/')) {
        setImage(file)
        setImagePreview(URL.createObjectURL(file))
      } else {
        alert(t('photoUpload.invalidImageFile'))
      }
      setLoadingImage(false)
    }
  }

  return (
    <Stack spacing={4} alignItems={'center'}>
      {loadingImage && <Loader />}
      {imagePreview && (
        <Box>
          <img
            src={imagePreview}
            alt={t('photoUpload.preview.alt')}
            style={{
              border: '1px solid #ccc',
              width: '200px',
              borderRadius: container === 'circle' ? '50%' : '0',
              ...(container === 'square' || container === 'circle'
                ? { aspectRatio: 1, objectFit: 'cover' }
                : {}),
            }}
          />
        </Box>
      )}
      <input
        accept="image/png, image/jpeg, image/heic, image/heif"
        type="file"
        id="photo-upload"
        style={{ display: 'none' }}
        onChange={handleImageChange}
      />
      <label htmlFor="photo-upload">
        {React.cloneElement(Button, {
          onClick: handleImageChange,
        })}
      </label>
    </Stack>
  )
}

export default PhotoUpload
